import { APP_SECTIONS, sectionForPath } from '@/config/app';

/**
 * One searchable page. `label` is what the user reads, `keywords` is text that
 * must be matchable but not shown — the palette renders it in a hidden span
 * because reka-ui's `CommandItem` filters on an item's rendered text content,
 * not on its props.
 */
export interface PageSearchEntry {
	path: string;
	icon: string;
	label: string;
	section: string;
	keywords: string;
}

export interface PageSearchGroup {
	section: string;
	entries: PageSearchEntry[];
}

/** Gate applied to every menu item; the real one resolves the route's minRole. */
export type PermitCheck = (item: { path: string; hidden?: boolean }) => boolean;

/**
 * Flattens every section menu into a de-duplicated, role-filtered list of pages
 * grouped by section, with the group for `currentPath`'s own section first.
 *
 * A page is grouped by the section its *path* belongs to rather than by the menu
 * it was found in. That is what stops `/user/profile` and `/settings` — declared
 * in three menus apiece — from appearing three times, and it files them under
 * Workspace rather than under whichever menu happened to be read first.
 *
 * Pure and free of vue-router and pinia imports so `npm run check:page-search`
 * can exercise it directly.
 */
export const buildPageCorpus = (
	isPermitted: PermitCheck,
	currentPath: string,
): PageSearchGroup[] => {
	const seen = new Set<string>();
	const bySection = new Map<string, PageSearchEntry[]>();

	// Seeded in declaration order so the groups come out in it too.
	for (const section of APP_SECTIONS) {
		bySection.set(section.label, []);
	}

	const add = (
		item: { title: string; path: string; hidden?: boolean },
		parent?: { title: string; icon: string; description?: string },
	) => {
		if (!isPermitted(item) || seen.has(item.path)) {
			return;
		}

		seen.add(item.path);

		const section = sectionForPath(item.path);
		const description = parent?.description ?? (item as { description?: string }).description;

		bySection.get(section.label)?.push({
			path: item.path,
			// Children carry no icon of their own; borrowing the parent's keeps
			// every row in the palette aligned.
			icon: parent?.icon ?? (item as { icon?: string }).icon ?? 'File',
			label: parent ? `${parent.title} › ${item.title}` : item.title,
			section: section.label,
			keywords: [section.label, parent?.title, description].filter(Boolean).join(' '),
		});
	};

	for (const section of APP_SECTIONS) {
		for (const group of Object.values(section.menu)) {
			for (const item of group.routes) {
				add(item);

				// Children are gated on their own route, not the parent's: a
				// readable section can hold an admin-only sub page.
				for (const child of item.children ?? []) {
					add(child, item);
				}
			}
		}
	}

	const groups = [...bySection.entries()]
		.map(([section, entries]) => {
			return { section, entries };
		})
		.filter((group) => {
			return group.entries.length > 0;
		});

	// The section the user is already in is the likeliest target, so it leads.
	const current = sectionForPath(currentPath).label;
	return groups.sort((a, b) => {
		return Number(b.section === current) - Number(a.section === current);
	});
};
